import {
  Combobox,
  Input,
  InputBase,
  InputBaseProps,
  Stack,
  Text,
  useCombobox,
} from "@mantine/core";
import { useLoaderData } from "@remix-run/react";
import { useEffect, useState } from "react";

import { useTranslation } from "~/hooks/use-typed-translation";
import { ComboboxItemPrice } from "~/types";
import { formatCurrency } from "~/utils/format-currency";

export function ServiceSummary({ label, price, duration, category, sessions }: ComboboxItemPrice) {
  const { t, i18n } = useTranslation();
  const loaderData = useLoaderData<{ currency: string }>();
  const currency = loaderData?.currency as string;

  return (
    <Stack component="span" gap={3}>
      <Text component="span" fz={14} fw={500} lh={1.4}>
        {label}
      </Text>
      <Text component="span" fz={15} fw={600}>
        {formatCurrency(price, i18n.language, currency)}
      </Text>
      <Text component="span" fz={12} c="dimmed" tt={"capitalize"}>
        {(category === "teleconsultation"
          ? t("profile-online-consultation")
          : category === "homecare"
            ? t("profile-home-visits")
            : category) + " - "}
        {duration} mins
      </Text>
      {(sessions ?? 1) > 1 && (
        <Text component="span" fz={12} c="dimmed">
          {t("profile-service-sessions", { amount: sessions })}
        </Text>
      )}
    </Stack>
  );
}

interface ConsultationServiceSelectProps
  extends Omit<InputBaseProps, "onChange"> {
  data?: ComboboxItemPrice[];
  placeholder?: string;
  onChange?: (value: string | null) => void;
  value?: string | null;
  error?: React.ReactNode;
}

export function ConsultationServiceSelect({
  data,
  placeholder,
  rightSection,
  onChange,
  value: controlledValue,
  error,
  ...rest
}: ConsultationServiceSelectProps) {
  const { t } = useTranslation();
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const [internalValue, setInternalValue] = useState<string | null>(
    controlledValue ?? null
  );

  // Sync internal state with controlled value
  useEffect(() => {
    if (controlledValue !== undefined) {
      setInternalValue(controlledValue);
    }
  }, [controlledValue]);

  const selectedOption = data?.find(
    item => item.value === (controlledValue ?? internalValue)
  );

  const options = data?.map(item => (
    <Combobox.Option value={item.value} key={item.value}>
      <ServiceSummary {...item} />
    </Combobox.Option>
  ));

  const handleOptionSubmit = (val: string) => {
    setInternalValue(val);
    onChange?.(val);
    combobox.closeDropdown();
  };

  return (
    <Combobox
      store={combobox}
      withinPortal={false}
      onOptionSubmit={handleOptionSubmit}>
      <Combobox.Target>
        <InputBase
          component="button"
          type="button"
          pointer
          styles={{ input: { whiteSpace: "normal", textAlign: "left", height: "auto", minHeight: 42 } }}
          rightSection={rightSection ? rightSection : <Combobox.Chevron />}
          onClick={() => combobox.toggleDropdown()}
          rightSectionPointerEvents="none"
          multiline
          error={error}
          {...rest}>
          {selectedOption ? (
            <ServiceSummary {...selectedOption} />
          ) : (
            <Input.Placeholder>
              {placeholder ? placeholder : t("select-service")}
            </Input.Placeholder>
          )}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown style={{ maxHeight: "min(320px, 60vh)", overflowY: "auto" }}>
        <Combobox.Options>{options}</Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
